import { useCallback, useEffect, useRef, useState } from "react";

import {
  fieldsMatch,
  PENDING_POLL_INTERVAL_MS,
  PENDING_TIMEOUT_MS,
  type PendingExpectation,
  type PendingMutation,
} from "./pendingMutations";

export type SettleOutcome =
  | { kind: "settled"; server: BankTransactionMessage }
  | { kind: "timeout" }
  | { kind: "gone" };

interface UsePendingMutationsArgs {
  fetchTransactionById: (
    wieldyId: string,
    signal?: AbortSignal,
  ) => Promise<BankTransactionMessage>;
  onSettled: (wieldyId: string, outcome: SettleOutcome) => void;
}

export function usePendingMutations({
  fetchTransactionById,
  onSettled,
}: UsePendingMutationsArgs) {
  const [pending, setPending] = useState<Map<string, PendingMutation>>(
    () => new Map(),
  );
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const inFlightRef = useRef<Set<string>>(new Set());

  const register = useCallback(
    (wieldyId: string, expectation: PendingExpectation) => {
      setPending((prev) => {
        const next = new Map(prev);
        next.set(wieldyId, {
          wieldyId,
          expected: expectation.expected,
          minSettleDurationMs: expectation.minSettleDurationMs,
          startedAt: Date.now(),
        });
        return next;
      });
    },
    [],
  );

  const cancel = useCallback((wieldyId: string) => {
    setPending((prev) => {
      if (!prev.has(wieldyId)) return prev;
      const next = new Map(prev);
      next.delete(wieldyId);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setPending((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  useEffect(() => {
    if (pending.size === 0) return undefined;

    const controller = new AbortController();

    const interval = setInterval(() => {
      Array.from(pendingRef.current.values()).forEach((mutation) => {
        const { wieldyId } = mutation;

        if (inFlightRef.current.has(wieldyId)) return;

        if (Date.now() - mutation.startedAt > PENDING_TIMEOUT_MS) {
          setPending((prev) => {
            if (!prev.has(wieldyId)) return prev;
            const next = new Map(prev);
            next.delete(wieldyId);
            return next;
          });
          onSettledRef.current(wieldyId, { kind: "timeout" });
          return;
        }

        inFlightRef.current.add(wieldyId);
        fetchTransactionById(wieldyId, controller.signal)
          .then((server) => {
            const current = pendingRef.current.get(wieldyId);
            if (!current) return;
            const elapsed = Date.now() - current.startedAt;
            const holdOpen =
              current.minSettleDurationMs !== undefined &&
              elapsed < current.minSettleDurationMs;
            if (!holdOpen && fieldsMatch(server, current.expected)) {
              setPending((prev) => {
                if (!prev.has(wieldyId)) return prev;
                const next = new Map(prev);
                next.delete(wieldyId);
                return next;
              });
              onSettledRef.current(wieldyId, { kind: "settled", server });
            }
          })
          .catch((err) => {
            if (controller.signal.aborted) return;
            if (err?.response?.status === 404) {
              setPending((prev) => {
                if (!prev.has(wieldyId)) return prev;
                const next = new Map(prev);
                next.delete(wieldyId);
                return next;
              });
              onSettledRef.current(wieldyId, { kind: "gone" });
              return;
            }
            // biome-ignore lint/suspicious/noConsole: intentional browser console output
            console.warn("Pending poll failed for", wieldyId, err);
          })
          .finally(() => {
            inFlightRef.current.delete(wieldyId);
          });
      });
    }, PENDING_POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [pending, fetchTransactionById]);

  return { pending, register, cancel, clearAll };
}
